import { CalculateRouteKpiRequest, CalculateRouteKpiResponse, Location, RouteKpi } from '@bringg/types';
import moment from 'moment';

import { RoutesPlannerTask } from '../../types/routes-planner-run';
import RoutesPlannerStore, { NO_ROUTE_ID } from '../../stores/routes-planner-store';
import { OptimizationKpi, OptimizationKpiData } from '../../stores/kpi/optimization-kpi-store';
import { optimizationKpiDataService } from './optimization-kpi-data-service';

const init = (
	rootStore: RoutesPlannerStore,
	fetchFunction: (request: CalculateRouteKpiRequest) => Promise<CalculateRouteKpiResponse>
) => {
	const fetchUpdatedKpiData = async (
		withOriginal: boolean,
		optimizationKpiData: OptimizationKpi
	): Promise<OptimizationKpi> => {
		let result: OptimizationKpi = Object.assign({}, optimizationKpiData);

		const promises = [];
		if (optimizationKpiData) {
			promises.push(fetchKpiData(false));
		}
		if (withOriginal || !optimizationKpiData) {
			promises.push(fetchKpiData(true));
		}
		const promiseResult = await Promise.all(promises);
		const manualData = promiseResult[0];

		if (promiseResult.length > 1) {
			const originalData = promiseResult[1];
			result = {
				original: originalData,
				manual: manualData
			};
		} else {
			if (!optimizationKpiData) {
				result = {
					original: manualData,
					manual: manualData
				};
			} else {
				result.manual = manualData;
			}
		}
		return result;
	};

	const fetchKpiData = async (isOriginal: boolean): Promise<OptimizationKpiData> => {
		const fetchedKpiData = await fetch(isOriginal ? rootStore.originalRunToTasks : rootStore.runToTasks);

		return {
			assignedCount: optimizationKpiDataService.assignedTasksCount(rootStore.runToTasks, rootStore.runsToDisplay),
			unassignedCount: optimizationKpiDataService.unassignedTasksCount(rootStore.runToTasks),
			totalRoutesDuration: optimizationKpiDataService.totalRoutesDuration(
				rootStore.routeInfoByIndex,
				routes(),
				rootStore.runsToDisplay,
				isOriginal
			),
			totalRoutesDistance: optimizationKpiDataService.totalRoutesDistance(
				rootStore.routeInfoByIndex,
				routes(),
				rootStore.runsToDisplay,
				isOriginal
			),
			stopsPerHour: optimizationKpiDataService.stopsPerHour(
				rootStore.routeInfoByIndex,
				isOriginal ? rootStore.originalRunToTasks : rootStore.runToTasks,
				routes(),
				rootStore.runsToDisplay,
				rootStore.routesById,
				isOriginal
			),
			timeFairness: fetchedKpiData.route_kpi.duration_per_route_fairness,
			intersections: fetchedKpiData.route_kpi.route_intersections
		};
	};

	const fetch = async (runToTasks: { [runId: number]: RoutesPlannerTask[] }) => {
		const routesById = rootStore.routesById;
		const routeInfoByIndex = rootStore.routeInfoByIndex;
		const runsToDisplay = rootStore.runsToDisplay;

		const routesKpi = [];
		Object.keys(runToTasks)
			.filter(runId => runId !== NO_ROUTE_ID.toString() && runsToDisplay.includes(Number(runId)))
			.forEach((runId: string) => {
				const tasks = runToTasks[runId];
				const locations = [];
				tasks.forEach(task => {
					const { lat, lng } = task.way_points[0];
					const location: Location = { lat, lng };
					locations.push(location);
				});
				const routeKpi: RouteKpi = {
					route: locations,
					start_time: moment
						.unix(routeInfoByIndex[runId]?.depot_etl || routesById[runId].depot_etl)
						.toISOString(),
					end_time: moment
						.unix(routeInfoByIndex[runId]?.depot_back_eta || routesById[runId].depot_back_eta)
						.toISOString()
				};
				routesKpi.push(routeKpi);
			});
		const request: CalculateRouteKpiRequest = {
			routes: routesKpi
		};
		return await fetchFunction(request);
	};

	const routes = () => {
		return Object.values(rootStore.routesById).filter(route => rootStore.runsToDisplay.includes(route.id));
	};

	return { fetchUpdatedKpiData, fetchKpiData, routes };
};

export const optimizationKpiFetchService = { init };
